// Material de apoyo — fotos, videos y PDFs que el asistente comparte por WhatsApp.
//
// Pedido de la administradora de Mi Mercadito (08-oct-2026): «Agrega al
// dashboard algún lugar donde pueda estar subiendo fotos, videos y pdf que
// puedas estar compartiendo». Ella sube el archivo con título, etiquetas y
// «cuándo mandarlo»; el asistente recibe la lista en cada turno (Orión,
// `<material>`) y lo manda cuando el tema coincide.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, Image as ImageIcon, Film, FileText, Loader2, Trash2, Pencil, Check, X,
  ExternalLink, AlertTriangle,
} from 'lucide-react';
import { bazaresApi as api, type Material } from '../api';

const ACEPTA = 'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,application/pdf';

const peso = (n: number) =>
  n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

function IconoTipo({ tipo, size = 18 }: { tipo: Material['tipo']; size?: number }) {
  if (tipo === 'imagen') return <ImageIcon size={size} />;
  if (tipo === 'video') return <Film size={size} />;
  return <FileText size={size} />;
}

function Vista({ m }: { m: Material }) {
  if (m.tipo === 'imagen') {
    return <img src={m.url} alt={m.titulo} loading="lazy" className="h-40 w-full object-cover bg-muted" />;
  }
  if (m.tipo === 'video') {
    return <video src={m.url} controls preload="metadata" className="h-40 w-full bg-muted object-cover" />;
  }
  return (
    <div className="h-40 w-full bg-muted flex flex-col items-center justify-center gap-2 text-muted-foreground">
      <FileText size={36} />
      <span className="text-xs">PDF</span>
    </div>
  );
}

function Tarjeta({ m, onCambio, onQuitar }: {
  m: Material;
  onCambio: (m: Material) => void;
  onQuitar: (id: number) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState(m.titulo);
  const [etiquetas, setEtiquetas] = useState(m.etiquetas.join(', '));
  const [cuando, setCuando] = useState(m.cuando_usar);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    setOcupado(true);
    setError(null);
    try {
      onCambio(await api.editarMaterial(m.id, {
        titulo, cuando_usar: cuando,
        etiquetas: etiquetas.split(',').map((x) => x.trim()).filter(Boolean),
      }));
      setEditando(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm(`¿Quitar «${m.titulo}»? El asistente ya no lo va a compartir.`)) return;
    setOcupado(true);
    try {
      await api.archivarMaterial(m.id);
      onQuitar(m.id);
    } catch (e) {
      setError((e as Error).message);
      setOcupado(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden flex flex-col">
      <Vista m={m} />
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        {editando ? (
          <div className="space-y-2">
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground" />
            <input value={etiquetas} onChange={(e) => setEtiquetas(e.target.value)} placeholder="stand, calendario…"
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground" />
            <textarea value={cuando} onChange={(e) => setCuando(e.target.value)} rows={2}
              placeholder="¿Cuándo debe mandarlo?"
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 text-foreground">
              <span className="mt-0.5 text-muted-foreground"><IconoTipo tipo={m.tipo} size={16} /></span>
              <span className="font-medium text-sm">{m.titulo}</span>
            </div>
            {m.etiquetas.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {m.etiquetas.map((t) => (
                  <span key={t} className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs">{t}</span>
                ))}
              </div>
            )}
            {m.cuando_usar && <p className="text-xs text-muted-foreground">Se manda: {m.cuando_usar}</p>}
            {!m.whatsapp_ok && (
              <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                WhatsApp no adjunta este video (debe ser MP4 de hasta 16 MB): se compartirá como liga.
              </p>
            )}
            <p className="text-xs text-muted-foreground">{peso(m.tamano)}</p>
          </>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="mt-auto flex items-center gap-1 pt-1">
          {editando ? (
            <>
              <button onClick={() => void guardar()} disabled={ocupado || !titulo.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">
                {ocupado ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Guardar
              </button>
              <button onClick={() => setEditando(false)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted">
                <X size={13} /> Cancelar
              </button>
            </>
          ) : (
            <>
              <a href={m.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted">
                <ExternalLink size={13} /> Ver
              </a>
              <button onClick={() => setEditando(true)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted">
                <Pencil size={13} /> Editar
              </button>
              <button onClick={() => void quitar()} disabled={ocupado}
                className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50">
                <Trash2 size={13} /> Quitar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function MaterialPanel() {
  const [items, setItems] = useState<Material[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [titulo, setTitulo] = useState('');
  const [etiquetas, setEtiquetas] = useState('');
  const [cuando, setCuando] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setItems(await api.material());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const elegir = (f: File | null) => {
    setArchivo(f);
    setAviso(null);
    // El nombre del archivo como título de arranque; casi siempre se cambia.
    if (f && !titulo) setTitulo(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim());
  };

  const subir = async () => {
    if (!archivo || !titulo.trim()) return;
    setSubiendo(true);
    setError(null);
    setAviso(null);
    try {
      const m = await api.subirMaterial(archivo, titulo.trim(), etiquetas, cuando);
      setItems((xs) => [m, ...xs]);
      setArchivo(null);
      setTitulo('');
      setEtiquetas('');
      setCuando('');
      if (input.current) input.current.value = '';
      setAviso(`Listo: «${m.titulo}» ya lo puede compartir el asistente (tarda hasta un minuto en verlo).`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Subir material</h2>
          <p className="text-sm text-muted-foreground">
            Fotos (JPG o PNG), videos (MP4) o PDFs que el asistente le comparte a la gente: el stand,
            ediciones anteriores, el calendario.
          </p>
        </div>

        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); elegir(e.dataTransfer.files?.[0] ?? null); }}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-4 py-6 text-sm text-muted-foreground hover:bg-muted/50"
        >
          <Upload size={22} />
          {archivo
            ? <span className="text-foreground">{archivo.name} · {peso(archivo.size)}</span>
            : <span>Arrastra aquí el archivo o haz clic para elegirlo</span>}
          <input ref={input} type="file" accept={ACEPTA} className="hidden"
            onChange={(e) => elegir(e.target.files?.[0] ?? null)} />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-foreground">Título *</span>
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Foto del stand"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-foreground">Etiquetas <span className="text-muted-foreground">(separadas por coma)</span></span>
            <input value={etiquetas} onChange={(e) => setEtiquetas(e.target.value)}
              placeholder="stand, qué incluye, medidas"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="text-foreground">¿Cuándo debe mandarlo el asistente?</span>
          <textarea value={cuando} onChange={(e) => setCuando(e.target.value)} rows={2}
            placeholder="Cuando pregunten qué incluye el stand o cómo es el espacio"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
        </label>

        <div className="flex items-center gap-3">
          <button onClick={() => void subir()} disabled={subiendo || !archivo || !titulo.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">
            {subiendo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {subiendo ? 'Subiendo…' : 'Subir'}
          </button>
          {aviso && <span className="text-sm text-emerald-600 dark:text-emerald-400">{aviso}</span>}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      {cargando ? (
        <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground">
          Todavía no hay material. Sube la foto del stand para empezar.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((m) => (
            <Tarjeta key={m.id} m={m}
              onCambio={(n) => setItems((xs) => xs.map((x) => (x.id === n.id ? n : x)))}
              onQuitar={(id) => setItems((xs) => xs.filter((x) => x.id !== id))} />
          ))}
        </div>
      )}
    </div>
  );
}
